/* Delivery choices are saved on the existing Shopify cart, never on a cloned checkout. */
(() => {
  if (window.TakebagDelivery) return;
  const root = window.Shopify?.routes?.root || '/';
  const endpoint = root + 'api/2026-07/graphql.json';
  const destinationKey = 'takebag.delivery.destination.v1', preferenceKey = 'takebag.delivery.choice.v1';
  const read = key => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; } };
  const write = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const digits = value => String(value || '').replace(/\D/g, '');
  const addresses = new Map();
  function deliveryTiming(description) {
    const parts = new Intl.DateTimeFormat('en-CA',{timeZone:'America/Sao_Paulo',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date());
    const part = type => Number(parts.find(p=>p.type===type).value);
    const date = new Date(Date.UTC(part('year'),part('month')-1,part('day')));
    const holidays = new Set(['01-01','04-21','05-01','09-07','10-12','11-02','11-15','11-20','12-25']);
    let count=0,first,last;
    while(count<9){
      date.setUTCDate(date.getUTCDate()+1);
      if(date.getUTCDay()===0 || date.getUTCDay()===6 || holidays.has(date.toISOString().slice(5,10)))continue;
      count++;
      const formatted = new Intl.DateTimeFormat('pt-BR',{timeZone:'UTC',day:'2-digit',month:'2-digit'}).format(date);
      if(count===7)first=formatted;
      if(count===9)last=formatted;
    }
    return 'Entrega estimada: '+first+' a '+last+' · 7 a 9 dias úteis';
  }
  async function resolveAddress(destination) {
    const zip = digits(destination.zip);
    if (!/^\d{8}$/.test(zip)) return destination;
    const remembered = read(destinationKey);
    if (remembered?.zip === zip && remembered.city) return {...destination,...remembered};
    if (!addresses.has(zip)) {
      addresses.set(zip,(async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(),4500);
        try {
          const response = await fetch('https://viacep.com.br/ws/'+zip+'/json/',{signal:controller.signal,credentials:'omit',referrerPolicy:'no-referrer'});
          if (!response.ok) return null;
          const data = await response.json();
          if (data.erro || digits(data.cep) !== zip) return null;
          return {zip,province:data.uf,address1:data.logradouro || '',neighborhood:data.bairro || '',city:data.localidade || ''};
        } catch (_) { return null; } finally { clearTimeout(timer); }
      })());
    }
    const address = await addresses.get(zip);
    if (!address) { addresses.delete(zip); return destination; }
    return {...destination,...address};
  }
  let cache, pending, chain = Promise.resolve(), busy = false, checkoutButton = null, checkoutButtonText = '';
  const RATE_QUERY = 'query TakebagCartDelivery($id: ID!) { cart(id:$id) { id checkoutUrl delivery { addresses { id selected oneTimeUse address { ... on CartDeliveryAddress { zip countryCode provinceCode address1 address2 city firstName lastName phone } } } } ...TakebagChoices @defer } } fragment TakebagChoices on Cart { deliveryGroups(first:10,withCarrierRates:true) { nodes { id deliveryOptions { handle code title description estimatedCost { amount currencyCode } } selectedDeliveryOption { handle } } pageInfo { hasNextPage endCursor } } }';
  const ADDRESS_MUTATION = 'mutation TakebagCartDestination($id: ID!, $addresses: [CartSelectableAddressInput!]!) { cartDeliveryAddressesAdd(cartId:$id,addresses:$addresses) { cart { id } userErrors { field message } } }';
  const REMOVE_ADDRESSES_MUTATION = 'mutation TakebagRemoveQuoteAddresses($id: ID!, $addressIds: [ID!]!) { cartDeliveryAddressesRemove(cartId:$id,addressIds:$addressIds) { cart { id delivery { addresses { id selected oneTimeUse } } } userErrors { field message } } }';
  const CHOICE_MUTATION = 'mutation TakebagCartChoice($id: ID!, $choices: [CartSelectedDeliveryOptionInput!]!) { cartSelectedDeliveryOptionsUpdate(cartId:$id,selectedDeliveryOptions:$choices) { cart { id checkoutUrl deliveryGroups(first:10) { nodes { id selectedDeliveryOption { handle } } pageInfo { hasNextPage endCursor } } } userErrors { field message } } }';
  async function api(query, variables) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(endpoint, {method:'POST',credentials:'omit',signal:controller.signal,headers:{'Content-Type':'application/json',Accept:'multipart/mixed; deferSpec=20220824, application/json'},body:JSON.stringify({query,variables})});
      if (!response.ok) throw new Error('delivery-api');
      const body = await response.text(), type = response.headers.get('content-type') || '';
      const boundary = type.match(/boundary="?([^";\s]+)"?/)?.[1];
      const parts = type.includes('multipart/mixed') && boundary ? body.split('--'+boundary).filter(part=>part.includes('{')).map(part=>JSON.parse(part.slice(part.indexOf('{'),part.lastIndexOf('}')+1))) : [JSON.parse(body)];
      let data = {};
      for (const part of parts) {
        if (part.errors?.length) throw new Error('delivery-api');
        Object.assign(data,part.data);
        for (const inc of part.incremental || []) {
          if (inc.errors?.length) throw new Error('delivery-api');
          if (inc.path?.join('.') === 'cart') Object.assign(data.cart,inc.data);
        }
      }
      return data;
    } finally { clearTimeout(timer); }
  }
  async function nativeCart() {
    const response = await fetch(root+'cart.js',{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
    if (!response.ok) throw new Error('cart');
    return response.json();
  }
  const fingerprint = cart => JSON.stringify([cart.token,cart.items.map(item=>[item.key,item.quantity,item.final_line_price]),cart.total_price,cart.discount_codes]);
  const match = (rate, choice) => !!choice && (choice.code ? rate.code === choice.code : rate.name === choice.name);
  function preference(zip) { const choice = read(preferenceKey); return choice?.zip === zip ? choice : null; }
  function choose(rate, zip) {
    write(preferenceKey,{zip,code:rate.code || '',name:rate.name,price:Number(rate.price),at:Date.now()});
    document.dispatchEvent(new CustomEvent('takebag:delivery-choice',{detail:{zip}}));
  }
  async function performQuote(destination) {
    destination = await resolveAddress(destination);
    if (read(destinationKey)?.zip === destination.zip) write(destinationKey,destination);
    const cart = await nativeCart();
    if (!cart.items.length || !cart.requires_shipping) return null;
    const key = fingerprint(cart), zip = digits(destination.zip);
    let quote;
    if (cache && cache.key === key && cache.zip === zip && Date.now()-cache.at < 120000) quote = cache;
    else {
      const id = 'gid://shopify/Cart/'+cart.token;
      let data = await api(RATE_QUERY,{id});
      if (!data.cart) throw new Error('cart-unavailable');
      const selectedAddress = data.cart.delivery.addresses.find(address=>address.selected)?.address;
      const sameDestination = digits(selectedAddress?.zip) === zip && selectedAddress?.countryCode === 'BR';
      if (!sameDestination || (destination.city && !selectedAddress?.city) || (destination.address1 && !selectedAddress?.address1) || (destination.neighborhood && !selectedAddress?.address2)) {
        const retained = sameDestination ? Object.fromEntries(['address1','address2','city','firstName','lastName','phone'].filter(key=>selectedAddress[key]).map(key=>[key,selectedAddress[key]])) : {};
        // Brazilian extended address format from @shopify/worldwide: empty line2 + WORD JOINER + neighborhood.
        const deliveryAddress = {zip,provinceCode:destination.province,countryCode:'BR',...(destination.address1 ? {address1:destination.address1} : {}),...(destination.neighborhood ? {address2:'\u2060'+destination.neighborhood} : {}),...(destination.city ? {city:destination.city} : {}),...retained};
        const update = (await api(ADDRESS_MUTATION,{id,addresses:[{address:{deliveryAddress},selected:true,oneTimeUse:true}]})).cartDeliveryAddressesAdd;
        if (!update?.cart || update.userErrors.length) throw new Error('address');
        data = await api(RATE_QUERY,{id});
      }
      const groups = data.cart.deliveryGroups;
      if (groups?.nodes.length !== 1 || groups.pageInfo.hasNextPage) throw new Error('delivery-groups');
      const group = groups.nodes[0];
      const rates = group.deliveryOptions.filter(rate=>Number.isFinite(Number(rate.estimatedCost.amount))).map(rate=>({handle:rate.handle,code:rate.code,name:rate.title,description:rate.description,price:Number(rate.estimatedCost.amount),currency:rate.estimatedCost.currencyCode}));
      if (!rates.length) throw new Error('no-rates');
      quote = {key,zip,id,groupId:group.id,rates,selected:group.selectedDeliveryOption?.handle,checkoutUrl:data.cart.checkoutUrl,at:Date.now()};
      cache = quote;
    }
    const wanted = preference(zip), rate = quote.rates.find(option=>match(option,wanted));
    if (rate && quote.selected !== rate.handle) {
      const result = (await api(CHOICE_MUTATION,{id:quote.id,choices:[{deliveryGroupId:quote.groupId,deliveryOptionHandle:rate.handle}]})).cartSelectedDeliveryOptionsUpdate;
      if (!result?.cart || result.userErrors.length || result.cart.deliveryGroups.nodes[0]?.selectedDeliveryOption?.handle !== rate.handle) throw new Error('selection');
      quote.selected = rate.handle;
      quote.checkoutUrl = result.cart.checkoutUrl;
    }
    // Never expose a quote for a cart that changed while carrier rates were loading.
    if (fingerprint(await nativeCart()) !== key) { cache = null; throw new Error('cart-changed'); }
    return quote;
  }
  function quote(destination) {
    const requestKey = JSON.stringify([destination,preference(digits(destination.zip))]);
    if (pending?.key === requestKey) return pending.promise;
    const promise = chain.catch(()=>{}).then(()=>performQuote(destination));
    chain = promise;
    pending = {key:requestKey,promise};
    promise.finally(()=>{if(pending?.promise === promise)pending=null;}).catch(()=>{});
    return promise;
  }
  async function clearQuoteAddresses() {
    const cart = await nativeCart();
    if (!cart.items.length || !cart.requires_shipping) return;
    const id = 'gid://shopify/Cart/'+cart.token;
    const data = await api('query TakebagQuoteAddresses($id: ID!) { cart(id:$id) { id delivery { addresses { id oneTimeUse } } } }',{id});
    if (!data.cart) throw new Error('cart-unavailable');
    const addressIds = data.cart.delivery.addresses.filter(address=>address.oneTimeUse).map(address=>address.id);
    if (!addressIds.length) return;
    const result = (await api(REMOVE_ADDRESSES_MUTATION,{id,addressIds})).cartDeliveryAddressesRemove;
    if (!result?.cart || result.userErrors.length) throw new Error('address-cleanup');
    cache = null;
  }
  function withDiscount(url) {
    let code; try { code = sessionStorage.getItem('bundle_discount_code'); } catch (_) {}
    return code ? root+'discount/'+encodeURIComponent(code)+'?redirect='+encodeURIComponent(url.pathname+url.search) : url.href;
  }
  async function attachDeliveryEstimate() {
    let cart = await nativeCart();
    const calculated = new Intl.DateTimeFormat('pt-BR',{timeZone:'America/Sao_Paulo',day:'2-digit',month:'2-digit'}).format(new Date());
    const estimate = deliveryTiming().replace('Entrega estimada: ','')+' (calculado em '+calculated+')';
    for(let index=0;index<cart.items.length;index++) {
      const item=cart.items[index];
      if(!item.requires_shipping || item.properties?.['Entrega estimada']===estimate)continue;
      const response=await fetch(root+'cart/change.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},signal:AbortSignal.timeout(8000),body:JSON.stringify({id:item.key,quantity:item.quantity,properties:{...item.properties,'Entrega estimada':estimate}})});
      if(!response.ok)throw new Error('delivery-estimate');
      cart=await response.json();
    }
    cache=null;
  }
  async function checkout(event, button) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy) return;
    busy = true;
    const original = button?.innerHTML;
    checkoutButton=button;checkoutButtonText=original;
    if (button) {button.disabled=true;button.setAttribute('aria-busy','true');button.textContent='PREPARANDO CHECKOUT...';}
    document.querySelectorAll('[data-tb-checkout-error]').forEach(node=>node.remove());
    try {
      if (window.TakebagCartMutation) await window.TakebagCartMutation;
      await attachDeliveryEstimate().catch(()=>{});
      await clearQuoteAddresses();
      const url = new URL(root+'checkout',location.origin);
      if (url.origin !== location.origin) throw new Error('checkout-origin');
      location.assign(withDiscount(url));
    } catch (_) {
      const message = document.createElement('p'); message.dataset.tbCheckoutError='';message.setAttribute('role','status');message.className='tb-shipping__status';
      message.textContent='Não conseguimos preparar o checkout agora. Tente novamente.'; (button?.parentElement || document.querySelector('main')).append(message);
      busy=false;
      if(button){button.disabled=false;button.removeAttribute('aria-busy');button.innerHTML=original;}
    }
  }
  window.addEventListener('submit',event=>{
    const form=event.target;
    if(form instanceof HTMLFormElement && (event.submitter?.name === 'checkout' || form.querySelector('input[name="checkout"]'))) checkout(event,event.submitter || document.querySelector('button[form="'+form.id+'"][name="checkout"]'));
  },true);
  window.addEventListener('click',event=>{
    const link=event.target.closest?.('a[href]');
    if(!link || link.hasAttribute('data-tb-native-checkout') || event.ctrlKey || event.metaKey || event.shiftKey || event.button) return;
    const url=new URL(link.href,location.origin);
    if(url.origin===location.origin && /\/checkout\/?$/.test(url.pathname)) checkout(event,link);
  },true);
  window.addEventListener('pageshow',event=>{if(event.persisted){busy=false;cache=null;if(checkoutButton){checkoutButton.disabled=false;checkoutButton.removeAttribute('aria-busy');checkoutButton.innerHTML=checkoutButtonText;}}});
  document.addEventListener('cart:updated',()=>{cache=null;});
  document.addEventListener('cart:refresh',()=>{cache=null;});
  window.TakebagDelivery={quote,choose,preference,match,resolveAddress,deliveryTiming};
  document.dispatchEvent(new CustomEvent('takebag:delivery-ready'));
})();
